import React, { useState } from "react";
import { Building2, Users, Wallet, FileText, Smartphone } from "lucide-react";

const PILLARS = [
  {
    id: "biens",
    icon: Building2,
    label: "Biens",
    tagline: "Gestion des propriétés",
    detail:
      "Cataloguez tous vos biens avec détails complets : type, surface, pièces, équipements. Géolocalisation, numérotation îlot, stockage de documents (titres fonciers, attestations). Suivi du statut en temps réel.",
    points: ["Catalogue complet", "Documents sécurisés", "Suivi des statuts", "Multi-propriétaires"],
  },
  {
    id: "locataires",
    icon: Users,
    label: "Locataires",
    tagline: "Gestion des occupants",
    detail:
      "Profils particuliers et professionnels. Dossiers complets avec pièces justificatives, garants, historique de communication. Vérifiez la fiabilité avant signature et réduisez les risques d'impayés.",
    points: ["Dossiers complets", "Gestion des garants", "Vérification fiabilité", "Historique comm."],
  },
  {
    id: "finances",
    icon: Wallet,
    label: "Finances",
    tagline: "Suivi des paiements",
    detail:
      "Suivi des loyers et charges en temps réel. Alertes visuelles pour retards de paiement. Génération automatique des quittances. Paiement Mobile Money (Orange Money, MTN). Rapports financiers exportables.",
    points: ["Alertes impayés", "Quittances auto", "Mobile Money", "Rapports détaillés"],
  },
  {
    id: "contrats",
    icon: FileText,
    label: "Contrats",
    tagline: "Gestion des baux",
    detail:
      "Créez et gérez tous vos contrats de location. Modèles personnalisables, signature électronique, renouvellements automatiques. Alertes avant échéance, historique des avenants, et archivage sécurisé.",
    points: ["Modèles de baux", "Signature électronique", "Alertes échéances", "Archivage sécurisé"],
  },
  {
    id: "mobile",
    icon: Smartphone,
    label: "App Mobile",
    tagline: "Pour vos locataires",
    detail:
      "Vos locataires payent depuis leur téléphone, consultent leur historique, signalent les problèmes avec photos, et communiquent directement avec vous. Une expérience moderne qui réduit vos appels.",
    points: ["Paiement mobile", "Signalement incidents", "Accès documents", "Messagerie directe"],
  },
];

const Ecosystem = () => {
  const [active, setActive] = useState("biens");
  const current = PILLARS.find((p) => p.id === active);
  const Icon = current.icon;

  return (
    <section
      id="fonctionnalites"
      data-testid="ecosystem-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        {/* <div className="flex items-center gap-3 mb-8">
          <span className="mikassa-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            Fonctionnalités complètes
          </span>
        </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16">
          <h2
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
            data-testid="ecosystem-title"
          >
            Cinq modules,<br />
            <span className="text-[#E8611A]">une seule plateforme.</span>
          </h2>
          {/* <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Biens, Locataires, Finances, App Mobile — chaque module conçu pour
            simplifier votre quotidien. Cliquez pour explorer.
          </p> */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Pillars column */}
          <div className="lg:col-span-5 flex flex-col">
            {PILLARS.map((p) => {
              const isActive = p.id === active;
              const PIcon = p.icon;
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  data-testid={`ecosystem-pillar-${p.id}`}
                  className={`text-left border-t border-[#0C2340]/15 py-6 md:py-8 group transition-all duration-500 ${
                    isActive ? "pl-6" : "pl-0 hover:pl-4"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <PIcon
                        size={22}
                        strokeWidth={1.4}
                        className={isActive ? "text-[#E8611A]" : "text-[#636E72]"}
                      />
                      <span
                        className={`font-heading font-black text-2xl md:text-4xl tracking-tighter uppercase transition-colors ${
                          isActive ? "text-[#0C2340]" : "text-[#0C2340]/40"
                        }`}
                      >
                        {p.label}
                      </span>
                    </div>
                    <span
                      className={`font-body text-[0.65rem] uppercase tracking-[0.24em] transition-all ${
                        isActive ? "text-[#E8611A] opacity-100" : "text-[#636E72] opacity-60"
                      }`}
                    >
                      {p.tagline}
                    </span>
                  </div>
                </button>
              );
            })}
            <div className="border-t border-[#0C2340]/15" />
          </div>

          {/* Detail panel */}
          <div
            key={active}
            className="lg:col-span-7 bg-[#0A1C33] text-white p-10 md:p-16 relative overflow-hidden animate-fade-up"
            data-testid="ecosystem-detail-panel"
          >
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-96 h-96 rotate-45 border border-white/10"
            />
            <div
              aria-hidden
              className="absolute -bottom-32 -left-32 w-96 h-96 rotate-45 border border-[#E8611A]/20"
            />
            <div className="relative">
              <Icon size={40} strokeWidth={1.2} className="text-[#E8611A] mb-6" />
              {/* <div className="font-body text-[0.65rem] uppercase tracking-[0.32em] text-white/60 mb-4">
                {current.tagline}
              </div> */}
              <h3 className="font-heading font-black text-white text-4xl md:text-6xl tracking-tighter mb-8 leading-none">
                {current.label}<span className="text-[#E8611A]">.</span>
              </h3>
              <p className="font-body text-white/85 text-base md:text-lg leading-relaxed mb-10 max-w-xl">
                {current.detail}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {current.points.map((pt) => (
                  <div
                    key={pt}
                    className="flex items-center gap-3 border-t border-white/15 pt-4"
                  >
                    <span className="mikassa-diamond" />
                    <span className="font-body text-white/90 text-sm">{pt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;
